import { storeToRefs } from 'pinia';
import { CobrowsePushEvent } from '~/app/cobrowse/protocol';
import type { SessionAnsweredPayload, SessionRequestedPayload } from '~/app/cobrowse/protocol';
import { useAuthStore } from '~/stores/authStore';
import { useCobrowseStore } from '~/stores/cobrowseStore';

export function useCobrowseRequestListener(): void {
    const { $websocket } = useNuxtApp();
    const { authed, tenant, tenants, isImpersonating } = storeToRefs(useAuthStore());
    const cobrowse = useCobrowseStore();

    const onRequested = (request: SessionRequestedPayload): void => {
        if (isImpersonating.value || String(request.userId) !== String(authed.value?.getId())) {
            return;
        }

        cobrowse.receiveRequest(request);
    };
    const onAnswered = (answer: SessionAnsweredPayload): void => cobrowse.dismissRequest(answer.sessionId);
    const onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible' && !isImpersonating.value) {
            cobrowse.fetchPendingRequest();
        }
    };

    // The sales person may target any tenant of a multi-tenant user, not only the one open here.
    const channels = computed(() => {
        const all = [tenant.value?.websocketChannel, ...(tenants.value?.items ?? []).map((item) => item.websocketChannel)];

        return [...new Set(all.filter((channel): channel is string => !!channel))];
    });

    const listen = (channel: string): void => {
        $websocket.private(channel).listen(`.${CobrowsePushEvent.Requested}`, onRequested);
        $websocket.private(channel).listen(`.${CobrowsePushEvent.Answered}`, onAnswered);
    };
    const stopListening = (channel: string): void => {
        $websocket.private(channel).stopListening(`.${CobrowsePushEvent.Requested}`, onRequested);
        $websocket.private(channel).stopListening(`.${CobrowsePushEvent.Answered}`, onAnswered);
    };

    watch(
        channels,
        (next, previous = []) => {
            previous.filter((channel) => !next.includes(channel)).forEach(stopListening);
            next.filter((channel) => !previous.includes(channel)).forEach(listen);
        },
        { immediate: true }
    );

    watch(
        () => tenant.value?.getId() ?? null,
        (tenantId) => {
            if (!tenantId || isImpersonating.value) {
                return;
            }

            useGetTenants().catch(() => undefined);
            cobrowse.onTenantReady();
            cobrowse.fetchPendingRequest();
        },
        { immediate: true }
    );

    document.addEventListener('visibilitychange', onVisibilityChange);

    onScopeDispose(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        channels.value.forEach(stopListening);
    });
}
